import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Monitor, Layout } from "lucide-react";

const ExamModeSelect = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state || {};

    const [countdown, setCountdown] = useState(null);
    useEffect(() => {
        const calc = () => {
            const diff = new Date("2026-10-14T09:00:00") - new Date();
            if (diff <= 0) { setCountdown({ over: true }); return; }
            setCountdown({
                days:  Math.floor(diff / 86400000),
                hours: Math.floor((diff % 86400000) / 3600000),
                mins:  Math.floor((diff % 3600000) / 60000),
                over:  false,
            });
        };
        calc();
        const i = setInterval(calc, 60000);
        return () => clearInterval(i);
    }, []);

    const launch = (mode) => {
        navigate(mode === "modern" ? "/exam" : "/exam-classic", { state });
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
            <nav className="border-b border-[var(--border-ui)] px-6">
                <div className="py-3.5 flex items-center justify-between">
                    <button onClick={() => navigate("/practice")} className="brand-btn">
                        tmua<span className="brand-prep">prep</span>
                    </button>
                    <div className="flex items-center gap-3">
                        {countdown && !countdown.over && (
                            <div className="hidden md:flex items-center gap-1.5 cursor-default select-none">
                                <span className="num text-xs font-semibold text-[var(--text)]">{countdown.days} days</span>
                                <span className="text-[11px] text-[var(--subtle-text)]">to TMUA</span>
                            </div>
                        )}
                        <button onClick={() => navigate(-1)} className="text-[var(--subtle-text)] hover:text-[var(--text)] text-sm transition-colors">
                            Back
                        </button>
                    </div>
                </div>
            </nav>

            <main className="paper-grid flex-1 flex items-center justify-center px-6 py-16">
                <div className="max-w-3xl w-full">
                    <div className="text-center mb-10">
                        <p className="eyebrow text-[#60a5fa] mb-3">Simulator</p>
                        <h1 className="font-display text-[38px] md:text-[46px] tracking-tight mb-2">Choose your interface</h1>
                        <p className="text-[var(--subtle-text)] text-sm">Same questions, different experience.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        {/* ── Modern ── */}
                        <button onClick={() => launch("modern")}
                            className="group text-left rounded-3xl overflow-hidden bg-[var(--surface)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.45] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(2,4,12,0.7),0_0_32px_rgba(96,165,250,0.1)]">
                            {/* mini preview — dark interface */}
                            <div className="relative h-44 bg-[var(--bg)] border-b border-[var(--border-ui)] p-5 overflow-hidden">
                                <div className="absolute inset-x-5 top-5 rounded-lg bg-[var(--surface-raised)] border border-[var(--border-ui)] p-3 group-hover:translate-y-[-2px] transition-transform duration-300">
                                    <div className="flex items-center justify-between mb-2.5">
                                        <div className="h-1.5 w-16 rounded-full bg-[var(--soft-fill)]" />
                                        <div className="num text-[9px] text-[#93c5fd]">71:38</div>
                                    </div>
                                    <div className="space-y-1.5 mb-3">
                                        <div className="h-1.5 w-full rounded-full bg-[var(--soft-fill)]" />
                                        <div className="h-1.5 w-3/4 rounded-full bg-[var(--soft-fill)]" />
                                    </div>
                                    <div className="space-y-1.5">
                                        {[0, 1, 2].map(n => (
                                            <div key={n} className={`h-4 rounded-md border flex items-center px-2 gap-1.5 ${n === 1 ? "border-[#60a5fa]/50 bg-[#60a5fa]/[0.1]" : "border-[var(--border-ui)]"}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${n === 1 ? "bg-[#60a5fa]" : "bg-[var(--soft-fill)]"}`} />
                                                <div className="h-1 flex-1 rounded-full bg-[var(--soft-fill)]" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* progress dots */}
                                <div className="absolute bottom-3 left-5 flex gap-1">
                                    {[...Array(8)].map((_, n) => (
                                        <span key={n} className={`w-1 h-1 rounded-full ${n < 4 ? "bg-[#60a5fa]/70" : "bg-[var(--soft-fill)]"}`} />
                                    ))}
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <Layout className="w-3.5 h-3.5 text-[#60a5fa]" strokeWidth={1.5} />
                                    <span className="text-base font-bold tracking-tight text-[var(--text)]">Modern</span>
                                </div>
                                <p className="text-xs text-[var(--muted-text)] leading-relaxed mb-4">Streamlined interface with flagging, navigation, and progress tracking.</p>
                                <span className="btn-primary !py-2 pointer-events-none">
                                    Launch <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                </span>
                            </div>
                        </button>

                        {/* ── Classic ── */}
                        <button onClick={() => launch("classic")}
                            className="group text-left rounded-3xl overflow-hidden bg-[var(--surface)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.45] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(2,4,12,0.7),0_0_32px_rgba(96,165,250,0.1)]">
                            {/* mini preview — PearsonVUE */}
                            <div className="relative h-44 bg-[var(--bg)] border-b border-[var(--border-ui)] p-5 overflow-hidden">
                                <div className="absolute inset-x-5 top-5 rounded-lg overflow-hidden border border-[var(--border-ui)] group-hover:translate-y-[-2px] transition-transform duration-300">
                                    <div className="bg-[rgb(0,109,170)] px-3 py-1.5 flex items-center justify-between">
                                        <div className="h-1.5 w-20 rounded-full bg-white/50" />
                                        <div className="num text-[8px] text-white/80">4 of 20</div>
                                    </div>
                                    <div className="bg-[rgb(70,120,190)] px-3 py-1 flex justify-end">
                                        <div className="h-1 w-10 rounded-full bg-white/40" />
                                    </div>
                                    <div className="bg-white p-3">
                                        <div className="space-y-1.5 mb-2.5">
                                            <div className="h-1.5 w-full rounded-full bg-black/[0.15]" />
                                            <div className="h-1.5 w-2/3 rounded-full bg-black/[0.15]" />
                                        </div>
                                        <div className="space-y-1.5">
                                            {[0, 1, 2].map(n => (
                                                <div key={n} className="flex items-center gap-1.5">
                                                    <div className={`w-2 h-2 rounded-full border ${n === 0 ? "border-[rgb(0,109,170)] bg-[rgb(0,109,170)]" : "border-black/[0.25]"}`} />
                                                    <div className="h-1 w-16 rounded-full bg-black/[0.1]" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bg-[rgb(0,109,170)] px-3 py-1.5 flex justify-between">
                                        <div className="h-1 w-8 rounded-full bg-white/40" />
                                        <div className="h-1 w-12 rounded-full bg-white/40" />
                                    </div>
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <Monitor className="w-3.5 h-3.5 text-[#60a5fa]" strokeWidth={1.5} />
                                    <span className="text-base font-bold tracking-tight text-[var(--text)]">Classic</span>
                                    <span className="eyebrow !text-[8px] text-[#60a5fa] bg-[#60a5fa]/[0.08] border border-[#60a5fa]/[0.25] px-2 py-0.5 rounded-full">1:1 replica</span>
                                </div>
                                <p className="text-xs text-[var(--muted-text)] leading-relaxed mb-4">The authentic PearsonVUE environment — exactly what you'll see on exam day.</p>
                                <span className="btn-primary !py-2 pointer-events-none">
                                    Launch <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                </span>
                            </div>
                        </button>
                    </div>
                </div>
            </main>

            <footer className="border-t border-[var(--border-ui)] px-6 py-5">
                <p className="text-center text-[var(--faint-text)] text-xs">© 2025 TMUA Prep. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default ExamModeSelect;
